import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FB_CONFIG_KEYS, fetchRemoteConfig } from '../firebase/remoteConfig';
import { parsePhonetoLink } from 'utils/getPhone';
import { PHONE, PHONE_LINK } from 'utils/constants';
import { ValidationError } from 'utils';
import useError from './useError';

type Props = {
  children: ReactNode;
};

export const FIREBASE_OVERDUE_PHONE_LINK = `${FB_CONFIG_KEYS.FIREBASE_OVERDUE_PHONE}_LINK`;

type FirebasePhones = {
  [FB_CONFIG_KEYS.FIREBASE_OVERDUE_PHONE]: string;
  [FIREBASE_OVERDUE_PHONE_LINK]: string;
};

type FirebaseContextType = {
  firebasePhones: FirebasePhones;
  fetchFirebaseData: () => void;
};

const FirebaseContext = createContext<FirebaseContextType>(
  {} as FirebaseContextType,
);

const useFirebase = (): FirebaseContextType => useContext(FirebaseContext);

const FirebaseProvider = ({ children }: Props): JSX.Element => {
  const [firebasePhones, setFirebasePhones] = useState<FirebasePhones>({
    [FB_CONFIG_KEYS.FIREBASE_OVERDUE_PHONE]: PHONE,
    [FIREBASE_OVERDUE_PHONE_LINK]: parsePhonetoLink(PHONE_LINK),
  });
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const { showError } = useError();

  const fetchFirebaseData = useCallback(async (): Promise<any> => {
    try {
      const firebaseValues = await fetchRemoteConfig();

      const overduePhone = firebaseValues[FB_CONFIG_KEYS.FIREBASE_OVERDUE_PHONE]
        ?._value as string;

      setFirebasePhones({
        [FB_CONFIG_KEYS.FIREBASE_OVERDUE_PHONE]: overduePhone,
        [FIREBASE_OVERDUE_PHONE_LINK]: parsePhonetoLink(overduePhone),
      });

      setIsDataLoaded(true);
    } catch (e) {
      showError(e as ValidationError);
    }
  }, [showError]);

  const FirebaseContextValue = useMemo(
    () => ({
      firebasePhones,
      fetchFirebaseData,
    }),
    [fetchFirebaseData, firebasePhones],
  );

  useEffect(() => {
    if (!isDataLoaded) fetchFirebaseData();
  }, [isDataLoaded]);

  return (
    <FirebaseContext.Provider value={FirebaseContextValue}>
      {children}
    </FirebaseContext.Provider>
  );
};

export { useFirebase as default, FirebaseProvider };
